// Hideo - the video dialog, without UI (src/components/dialogs/VideoDialog.vue, state in src/stores/video.ts): the
// form of a new or saved video, the fields of each content type, the work dates line, the checklist, the scripts of the
// open video, save (with the thumbs-up decision) and delete.
// The content Type is the first choice in the dialog: five fixed Types (VIDEO_TYPES, src/data/contentTypes.ts), each with
// its own fields in order and its own second tab (SeoPanel.vue). KIND_INFO: look and texts of each Type.
// Save starts from the saved video and overwrites the fields the dialog edits, in the same order as before (fields it
// does not know, e.g. from a newer version of the app, are kept). Stored names never change: video.notes ("Appunti"),
// video.seriesId (the format), reelProva 'Sì' / 'No', the status and priority values.
import { t, tn } from '../i18n'
import { STATUS_KEYS, STATUS_READY, fixStatus, fixedType, formatKind, kindStatuses, statusName } from '../data/contentTypes'
import type { ChecklistItem, FormatName, Kind, ProfileData, Script, Video, VideoSeo } from '../data/types'
import { tlDur, tlEnd, tlParse } from './agenda'
import { tlFmt, tlMarkDone, tlNice } from './timeline'
import { seriesOf } from './formats'
import { cleanPubLinks, type PubLinks } from './published'
import { normUrl } from '../utils/url'
import { WORK_TIME_RE } from '../utils/date'
import { validProject } from './projects'
import { fountainTabHtml } from './editor/fountain'


// ===== Content types =====
interface KindInfo {
  icon: string
  name: string
  /** One line in Settings › Plan */
  short: string
  /** Line under the Type pills */
  hint: string
  /** Icon and title of the first group */
  group: [icon: string, title: string]
  /** Title of the second group and the script label ('' = no script: Post) */
  script: [title: string, label: string]
  /** Placeholder of the image box */
  thumb: string
  /** Label of the end-of-work date */
  end: string
  /** Last box: icon, title, text */
  next: [icon: string, title: string, text: string]
}
export const KIND_INFO: Record<Kind, KindInfo> = {
  h: { icon: 'fa-tv', name: t('Horizontal'), short: t('Long horizontal video: script, 16:9 thumbnail and SEO for YouTube.'),
    hint: t('Horizontal video: the second tab has the SEO for YouTube (title, description, chapters, tags).'),
    group: ['fa-clapperboard', t('The video')], script: [t('Script and notes'), t('SCRIPT')], thumb: t('Add thumbnail (optional)'), end: t('End of work · publishing (day and time)'),
    next: ['fa-magnifying-glass-chart', t('Last step: SEO'), t('Got everything in? Optimise the title, description, chapters and tags for YouTube.')] },
  v: { icon: 'fa-mobile-screen-button', name: t('Vertical'), short: t('Shorts, Reels and TikTok: 9:16 cover, hook, length and caption.'),
    hint: t('Vertical video (Short, Reel, TikTok): 9:16 cover and, in the second tab, hook, length and caption.'),
    group: ['fa-mobile-screen-button', t('The Short')], script: [t('Script and notes'), t('SCRIPT')], thumb: t('Vertical 9:16 cover (optional)'), end: t('End of work · publishing (day and time)'),
    next: ['fa-mobile-screen-button', t('Last step: publishing'), t('Check hook and length, write the title and caption to copy to Shorts, TikTok and Instagram.')] },
  post: { icon: 'fa-image', name: t('Image'), short: t('Photo or carousel: image and caption, no script.'),
    hint: t('Post (photo or carousel): no script; write the caption in the second tab.'),
    group: ['fa-image', t('The post')], script: [t('Notes'), ''], thumb: t('Post image or first slide (optional)'), end: t('End of work · publishing (day and time)'),
    next: ['fa-image', t('Last step: the caption'), t('Write the caption with the keyword in the first line and copy it.')] },
  live: { icon: 'fa-tower-broadcast', name: t('Live stream'), short: t('Live stream: a run sheet instead of the script, time, title and description.'),
    hint: t('Live stream: a run sheet instead of the script; title, description, thumbnail and time in the second tab.'),
    group: ['fa-tower-broadcast', t('The live stream')], script: [t('Run sheet and notes'), t('RUN SHEET')], thumb: t('Live stream thumbnail (optional)'), end: t('Start of the live stream (day and time)'),
    next: ['fa-tower-broadcast', t('Last step: the announcement'), t('Title, description, thumbnail and time of the live stream.')] },
  blog: { icon: 'fa-newspaper', name: t('Article'), short: t('Article written in the editor: cover, reading time and SEO for the web.'),
    hint: t('Blog: write the article in the editor; the SEO for Google (title, description, address) is in the second tab.'),
    group: ['fa-newspaper', t('The article')], script: [t('Article and notes'), t('ARTICLE')], thumb: t('Cover image (optional)'), end: t('End of work · publishing (day and time)'),
    next: ['fa-magnifying-glass', t('Last step: SEO for the web'), t('Title for Google, meta description, address and alternative text of the cover.')] }
}

/** Vertical (9:16) and post (4:5) images go on the side, with the fields on their right; the others on top, wide */
export const sideImage = (k: Kind): boolean => k === 'v' || k === 'post'
/** Width of the side image column (CSS), '' for the wide image */
export const sideWidth = (k: Kind): string => (k === 'v' ? 'clamp(6.5rem, 30%, 9.5rem)' : k === 'post' ? 'clamp(7.5rem, 36%, 12rem)' : '')
/** CSS aspect-ratio of the image box, '' = the fixed height of the wide box */
export const imageRatio = (k: Kind): string => (k === 'v' ? '9 / 16' : k === 'post' ? '4 / 5' : '')

/** Status pills of a Type: the stored values in the Type's words; statuses the Type does not use are hidden (Blog) */
export const statusOptions = (k: Kind): { value: string; label: string; hidden: boolean }[] =>
  STATUS_KEYS.map(s => ({ value: s, label: statusName(k, s), hidden: !(kindStatuses(k) as readonly string[]).includes(s) }))

// ===== Form =====
export interface VideoForm {
  /** The saved video's id, null for a new one */
  id: string | null
  title: string
  /** External script link */
  link: string
  /** Script chosen among the video's scripts ('' = none) */
  scriptId: string
  reelProva: string
  publishDate: string
  publishTime: string
  /** Work start as in the field (YYYY-MM-DDTHH:MM or '') */
  workStart: string
  status: string
  /** Content type (one of the fixed five) */
  format: FormatName
  priority: string
  projectId: string
  /** Format of the video (data.series id); may belong to another project until saved (then it is dropped) */
  seriesId: string
  thumbnail: string
  voiceNote: string
  /** Ticks shown (the video's saved ones, including items removed from Settings) */
  checklist: Record<string, boolean>
  /** "Appunti", with @mentions */
  notes: string
  /** Linked sources and saved videos (the video's references, VideoRefs.vue) */
  sourceIds: string[]
  savedIds: string[]
}
/** What a new video can start with (a day or a time slot of the Calendar; the format, its project and type: "New video"
 *  of a format card) */
export type VideoPrefill = Partial<Pick<VideoForm, 'publishDate' | 'publishTime' | 'workStart' | 'status' | 'title' | 'seriesId' | 'projectId' | 'format'>>

export const formKind = (f: Pick<VideoForm, 'format'>): Kind => formatKind(f.format)

/** Scripts of the open dialog: the video's own and those created or attached now (they become the video's on save) */
export const dialogScripts = (d: Pick<ProfileData, 'scripts'>, vid: string | null, session: readonly string[], attached: readonly string[]): Script[] =>
  d.scripts.filter(s => (!!vid && s.videoId === vid) || session.includes(s.id) || attached.includes(s.id))
/** Scripts without a video (not chosen by any video, not taken in this dialog): they can be attached here */
export function orphanScripts(d: Pick<ProfileData, 'scripts' | 'videos'>, session: readonly string[], attached: readonly string[]): Script[] {
  const vids = new Set(d.videos.map(v => v.id)), used = new Set(d.videos.map(v => v.scriptId))
  return d.scripts.filter(s => !vids.has(s.videoId) && !used.has(s.id) && !session.includes(s.id) && !attached.includes(s.id))
}
/** The script shown in the picker: the chosen one, else the current one, else the first; '' without scripts */
export function pickScript(list: readonly Script[], sel: string, current: string): string {
  const sc = list.find(x => x.id === sel) || list.find(x => x.id === current) || list[0]
  return sc ? sc.id : ''
}

/** The dialog's starting fields: the saved video, or a new one (Long Form, Idea, Medium) in the project of new items with
 *  the format of the active format filter; prefill = a Calendar day / time slot */
export function videoForm(d: ProfileData, id: string | null, o: { newProject: string; filterSeries: string; prefill?: VideoPrefill }): VideoForm {
  const v = id ? d.videos.find(x => x.id === id) : undefined
  const f: VideoForm = v ? {
    id, title: v.title || '', link: v.link || '', scriptId: pickScript(dialogScripts(d, id, [], []), v.scriptId || '', ''),
    reelProva: v.reelProva || 'No', publishDate: v.publishDate || '', publishTime: v.publishTime || '', workStart: tlParse(v.workStart) ? v.workStart as string : '',
    status: v.status || 'Idea', format: fixedType(v.format || d.settings.formats[0]), priority: v.priority || 'Media',
    projectId: validProject(d, v.projectId, o.newProject), seriesId: v.seriesId || '', thumbnail: v.thumbnail || '', voiceNote: v.voiceNote || '',
    checklist: { ...(v.checklist || {}) }, notes: v.notes || '', sourceIds: [...(v.sourceIds || [])], savedIds: [...(v.savedIds || [])]
  } : {
    id, title: '', link: '', scriptId: '', reelProva: 'No', publishDate: '', publishTime: '', workStart: '', status: 'Idea',
    format: fixedType(d.settings.formats[0]), priority: 'Media', projectId: o.newProject,
    seriesId: id ? '' : o.filterSeries, thumbnail: '', voiceNote: '', checklist: {}, notes: '', sourceIds: [], savedIds: []
  }
  if (o.prefill) Object.assign(f, o.prefill)
  // A status the Type does not use becomes the nearest one (Blog)
  f.status = fixStatus(formKind(f), f.status)
  return f
}

/** Another Type picked: a status it does not use becomes the nearest one */
export function setFormat(f: VideoForm, format: string): void {
  f.format = fixedType(format)
  const ok = fixStatus(formKind(f), f.status)
  if (f.status && ok !== f.status) f.status = ok
}
/** Another project picked: a format of another project is dropped */
export function projectChanged(d: Pick<ProfileData, 'series'>, f: VideoForm): void {
  const s = seriesOf(d, f.seriesId)
  if (s && s.projectId !== f.projectId) f.seriesId = ''
}

// ===== Work dates =====
/** Work start from the field: "YYYY-MM-DDTHH:MM" or '' */
export const workInput = (x: string): string => (WORK_TIME_RE.test((x || '').slice(0, 16)) ? x.slice(0, 16) : '')
/** The line under the dates: [kind, HTML] (translated text; its values are dates and durations, no user text) */
type WorkInfoKind = 'endBeforeStart' | 'deliveredLate' | 'deliveredOnTime' | 'willBeLate' | 'willBeOnTime' | 'overdue' | 'duration' | 'noStart' | 'noEnd'
export function workInfo(f: Pick<VideoForm, 'workStart' | 'publishDate' | 'publishTime' | 'status'>, saved: Video | null, now: Date): { kind: WorkInfoKind; text: string } {
  const s = tlParse(workInput(f.workStart)), e = tlEnd(f), st = f.status
  const done = saved && saved.status === STATUS_READY ? tlParse(saved.doneAt) : null
  if (s && e && e <= s) return { kind: 'endBeforeStart', text: t('The end of work is before the start') }
  if (st === STATUS_READY && done && e) return done > e
    ? { kind: 'deliveredLate', text: t('Delivered {date}: <b class="text-brand">{time} after the end of work</b>', { date: tlNice(done), time: tlDur(+done - +e) }) }
    : { kind: 'deliveredOnTime', text: t('Delivered {date}: on time', { date: tlNice(done) }) }
  if (st === STATUS_READY && e) return now > e
    ? { kind: 'willBeLate', text: t('When you save, it is marked as delivered now: <b class="text-brand">{time} after the end of work</b>', { time: tlDur(+now - +e) }) }
    : { kind: 'willBeOnTime', text: t('When you save, it is marked as delivered now, on time') }
  if (e && e < now) return { kind: 'overdue', text: t('<b class="text-brand">Overdue by {time}</b>. When it is ready move it to "Ready/Published", or move the end of work.', { time: tlDur(+now - +e) }) }
  if (s && e) return { kind: 'duration', text: t('Work duration: {time}', { time: tlDur(+e - +s) }) }
  if (e) return { kind: 'noStart', text: t('Choose the start too, to see in the Timeline how long the work takes.') }
  return { kind: 'noEnd', text: t('The end of work is the publish day (and time): Calendar, Timeline and "To do and overdue" use it.') }
}

// ===== Save =====
/** Why the form cannot be saved: 'title' (no title), 'workStart' (work start not before the end of work), or null */
export function formError(f: VideoForm): 'title' | 'workStart' | null {
  if (!f.title.trim()) return 'title'
  const s = workInput(f.workStart), e = tlEnd(f), sd = tlParse(s)
  if (s && e && sd && e <= sd) return 'workStart'
  return null
}
/** The video's checklist: the saved ticks (items removed in Settings keep their value: if they come back, the tick is
 *  still there) with the current items' ticks over them */
export const mergeChecklist = (saved: Record<string, boolean> | undefined, items: readonly ChecklistItem[], ticks: Record<string, boolean>): Record<string, boolean> =>
  Object.assign({}, saved || {}, Object.fromEntries(items.map(it => [it.id, !!ticks[it.id]])))

/** What the dialog's other tabs hold (video store: seo, published, pub): the SEO card, the published state and the
 *  "Released" tab draft */
interface VideoTabsDraft {
  seo: VideoSeo
  published: boolean
  pub: { links: PubLinks | Record<string, string>; at: string; note: string }
}
export interface SavedVideo {
  video: Video
  /** The video before the save (null = new) */
  before: Video | null
  /** The published state changed (toast instead of the thumbs-up) */
  pubChanged: boolean
}
/** Writes the video into the data (new: added at the end; saved: replaced in place, or nothing if it was deleted in the
 *  meantime) and gives the scripts created or attached in the dialog to it. projectId must be valid (validProject). */
export function saveVideo(d: ProfileData, f: VideoForm, x: VideoTabsDraft & { projectId: string; scripts: readonly string[]; now?: Date }): SavedVideo {
  const now = x.now || new Date(), kind = formKind(f)
  const was = f.id ? d.videos.find(v => v.id === f.id) || null : null
  const video: Video = {
    id: f.id || 'v_' + +now,
    title: f.title.trim(),
    link: normUrl(f.link),
    scriptId: f.scriptId,
    reelProva: kind === 'v' ? f.reelProva as Video['reelProva'] : 'No', publishDate: f.publishDate, publishTime: f.publishTime,
    workStart: workInput(f.workStart),
    status: fixStatus(kind, f.status),
    format: fixedType(f.format),
    projectId: x.projectId,
    seo: x.seo,
    seriesId: seriesOf(d, f.seriesId)?.projectId === x.projectId ? f.seriesId : '',
    priority: f.priority as Video['priority'],
    thumbnail: f.thumbnail,
    voiceNote: f.voiceNote,
    checklist: mergeChecklist(was?.checklist, d.settings.checklist, f.checklist),
    notes: f.notes.trim(),
    sourceIds: f.sourceIds.filter(id => d.sources.some(s => s.id === id)),
    savedIds: f.savedIds.filter(id => d.savedVideos.some(s => s.id === id))
  }
  // Scripts created or re-linked in this dialog now belong to this video
  x.scripts.forEach(id => { const s = d.scripts.find(sc => sc.id === id); if (s) s.videoId = video.id })
  // Published videos: a published video is always "Pronto/Pubblicato"
  video.published = !!x.published
  video.publishedAt = x.published ? ((x.pub.at && tlParse(x.pub.at) && x.pub.at) || (was && was.publishedAt) || tlFmt(now)) : ''
  video.pubLinks = cleanPubLinks(x.pub.links)
  video.pubNote = String(x.pub.note || '').trim()
  if (x.published) video.status = STATUS_READY
  const pubChanged = !!was && !!was.published !== video.published
  tlMarkDone(video, was, now)   // actual delivery: when the video becomes "Pronto/Pubblicato"
  // Fields the dialog does not edit stay as they were (they used to be lost on save)
  if (was) for (const k of Object.keys(was)) if (!(k in video)) video[k] = was[k]
  if (f.id) {
    const i = d.videos.findIndex(v => v.id === f.id)
    if (i !== -1) d.videos[i] = video
  } else d.videos.push(video)
  return { video, before: was, pubChanged }
}
/** Thumbs-up only for important moments (rule 7): a new video in the plan, a video just moved to Ready; null = none */
export function celebration(before: Pick<Video, 'status'> | null | undefined, after: Pick<Video, 'status'>): string | null {
  if (after.status === STATUS_READY && (!before || before.status !== STATUS_READY)) return t('Video ready to publish!')
  if (!before) return t('Video added to the plan')
  return null
}

// ===== Close and delete =====
/** Scripts created in the dialog that no video took (the video was not saved) */
export function sessionOrphans(d: Pick<ProfileData, 'scripts' | 'videos'>, session: readonly string[]): Script[] {
  const used = new Set(d.videos.map(v => v.scriptId)), vids = new Set(d.videos.map(v => v.id))
  return d.scripts.filter(s => session.includes(s.id) && !vids.has(s.videoId) && !used.has(s.id))
}
/** The script has some text (an empty one is deleted without asking) */
export const scriptHasText = (s: Pick<Script, 'tabs'>, plain: (html: string) => string): boolean => (s.tabs || []).some(tab => plain(tab.html).trim() !== '')

/** The scripts a deleted video would leave without a video: its own, the one it chose, those created in the dialog,
 *  unless another video chose them. Read on the data with the video already removed. */
export const deletedVideoScripts = (d: Pick<ProfileData, 'scripts' | 'videos'>, vid: string, scriptId: string, session: readonly string[]): Script[] =>
  d.scripts.filter(s => (s.videoId === vid || (!!scriptId && s.id === scriptId) || session.includes(s.id)) && !d.videos.some(x => x.scriptId === s.id))
/** Removes the video; its scripts (deletedVideoScripts) are deleted (dropScripts) or kept without a video.
 *  Returns false if the video does not exist. */
export function deleteVideo(d: ProfileData, vid: string, session: readonly string[], dropScripts: boolean): boolean {
  const v = d.videos.find(x => x.id === vid)
  if (!v) return false
  d.videos = d.videos.filter(x => x.id !== vid)
  const mine = deletedVideoScripts(d, vid, v.scriptId || '', session)
  if (dropScripts) d.scripts = d.scripts.filter(s => !mine.includes(s))
  else mine.forEach(s => { s.videoId = '' })
  return true
}
/** Question before deleting the scripts of a deleted video */
export const deleteScriptsQuestion = (n: number): string =>
  tn(n, 'Also delete the script of this video?\n\nIf you keep it, you can find it by opening another video › Create script › "Script without video".',
    'Also delete the {n} scripts of this video?\n\nIf you keep them, you can find them by opening another video › Create script › "Script without video".')

// ===== New script from the dialog =====
/** A new script's starting point: its name, its first tab's HTML, and 'fountain' for a screenplay */
export interface ScriptStart { name: string; html: string; mode?: 'fountain' }
/** The built-in screenplay: a title page and a short scene, in the UI language (the scene heading words, the
 *  transition and the Fountain keys are the screenplay standard and stay in English) */
const sampleScreenplay = (): string => [
  'Title: ' + t('My short film'), 'Credit: ' + t('Written by'), 'Author: ' + t('Your name'), '',
  'INT. ' + t('KITCHEN') + ' - NIGHT', '', t('Anna stirs a pot. The radio plays softly.'), '',
  'ANNA', '(' + t('without turning around') + ')', t('You are late again.'), '',
  'MARCO', t('The bus never came.'), '', 'CUT TO:', ''
].join('\n')
/** Built-in script templates: [name, html, mode], created in the UI language */
export const builtinTemplates = (): [string, string, 'fountain'?][] => [
  [t('Hook · Body · CTA'), `<h2>${t('Hook (0-5 sec)')}</h2><p></p><h2>${t('Context')}</h2><p></p><h2>${t('Development')}</h2><p></p><h2>${t('Closing and CTA')}</h2><p></p>`],
  [t('Short Form (60 sec)'), `<h2>${t('Hook')}</h2><p></p><h2>${t('Point 1')}</h2><p></p><h2>${t('Point 2')}</h2><p></p><h2>${t('Surprise ending')}</h2><p></p><h2>${t('CTA')}</h2><p></p>`],
  [t('Long Form (story)'), `<h2>${t('Intro')}</h2><p></p><h2>${t('Context')}</h2><p></p><h2>${t('Main part')}</h2><p></p><h2>${t('Plot twist')}</h2><p></p><h2>${t('Conclusion')}</h2><p></p><h2>${t('References')}</h2><ul><li></li></ul>`],
  [t('Screenplay (Fountain)'), fountainTabHtml(sampleScreenplay()), 'fountain']
]
/** Starting point of a new script: '' blank · 'b:<n>' built-in template · 't:<id>' user template · 'i:<id>' hook ·
 *  file = { name, html } from a .md / .txt. hookHtml turns a hook into the script's HTML. → { name, html } */
export function scriptStart(d: ProfileData, v: string, file: ScriptStart | null, hookHtml: (title: string, content: string) => string): ScriptStart {
  if (file) return file.mode === 'fountain' ? { name: file.name, html: file.html, mode: 'fountain' } : { name: file.name, html: file.html }
  if (v.startsWith('b:')) { const tpl = builtinTemplates()[+v.slice(2)]; if (tpl) return tpl[2] ? { name: tpl[0], html: tpl[1], mode: tpl[2] } : { name: tpl[0], html: tpl[1] } }
  if (v.startsWith('t:')) { const tpl = d.templates.find(x => x.id === v.slice(2)); if (tpl) return tpl.mode === 'fountain' ? { name: tpl.name, html: tpl.html, mode: 'fountain' } : { name: tpl.name, html: tpl.html } }
  if (v.startsWith('i:')) { const hook = d.vault.find(x => x.id === v.slice(2)); if (hook) return { name: hook.title, html: hookHtml(hook.title, hook.content || '') } }
  return { name: '', html: '' }
}
/** Adds a new script to the open video (videoId '' while the video is new: it gets it on save), named after the video
 *  (or the starting point) and numbered when the video already has scripts. Returns its id. */
export function addVideoScript(d: ProfileData, vid: string | null, videoTitle: string, start: ScriptStart, count: number, now = Date.now()): string {
  const id = 'sc_' + now, name = videoTitle.trim() || start.name
  const sc: Script = { id, title: (name || t('New script')) + (count ? ' · ' + (count + 1) : ''), videoId: vid || '', tabs: [{ id: 'tb_' + now, name: t('First draft'), html: start.html }] }
  if (start.mode === 'fountain') sc.mode = 'fountain'
  d.scripts.push(sc)
  return id
}
